import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

import env from '../env';
import { LOCALSTORAGE_SSO_HANDOFF_SESSION } from '../util/constants';

/**
 * Talks to Cognito's InitiateAuth/RespondToAuthChallenge directly instead of
 * going through Amplify's `Auth` class. `aws-amplify@5`/`amazon-cognito-identity-js@6.3.7`
 * predate Cognito's native EMAIL_OTP passwordless factor (USER_AUTH flow) and have no
 * code path for it. These two operations are unauthenticated Cognito APIs (the same way
 * Amplify itself calls them - see amazon-cognito-identity-js's Client.js), so a plain
 * fetch is all that's needed to drive the flow.
 */
const COGNITO_IDP_ENDPOINT = `https://cognito-idp.${env.REACT_APP_AWS_COGNITO_REGION}.amazonaws.com/`;

const userPool = new CognitoUserPool({
  UserPoolId: env.REACT_APP_AWS_COGNITO_USER_POOL_ID,
  ClientId: env.REACT_APP_AWS_COGNITO_USER_POOL_WEBCLIENT_ID,
});

export class CognitoIdpError extends Error {
  type: string;

  constructor(type: string, message: string) {
    super(message);
    this.type = type;
  }
}

type AuthenticationResult = {
  IdToken: string;
  AccessToken: string;
  RefreshToken: string;
};

type InitiateAuthResponse = {
  ChallengeName?: string;
  Session?: string;
  AuthenticationResult?: AuthenticationResult;
};

const cognitoIdpRequest = async <T>(operation: string, body: Record<string, unknown>): Promise<T> => {
  const response = await fetch(COGNITO_IDP_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok) {
    const type = (data.__type || '').split('#').pop() || 'UnknownError';
    throw new CognitoIdpError(type, data.message || 'Cognito request failed');
  }

  return data as T;
};

export interface EmailOtpChallenge {
  username: string;
  session: string;
}

export class EmailOtpVerificationError extends Error {
  reason: 'INVALID_CODE' | 'EXPIRED_CODE' | 'UNKNOWN';

  constructor(reason: 'INVALID_CODE' | 'EXPIRED_CODE' | 'UNKNOWN') {
    super(reason);
    this.reason = reason;
  }
}

/*
 * Starts (or restarts, for resend) the EMAIL_OTP challenge for a user known to exist
 * (the email step already verified this before offering "sign in with code").
 */
export const requestEmailOtp = async (email: string): Promise<EmailOtpChallenge> => {
  const result = await cognitoIdpRequest<InitiateAuthResponse>('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: env.REACT_APP_AWS_COGNITO_USER_POOL_WEBCLIENT_ID,
    AuthParameters: {
      USERNAME: email,
      PREFERRED_CHALLENGE: 'EMAIL_OTP',
    },
  });

  // Only expected if PREFERRED_CHALLENGE isn't honoured for this user - explicitly
  // select EMAIL_OTP to progress to the actual challenge.
  if (result.ChallengeName === 'SELECT_CHALLENGE') {
    const selected = await cognitoIdpRequest<InitiateAuthResponse>('RespondToAuthChallenge', {
      ChallengeName: 'SELECT_CHALLENGE',
      ClientId: env.REACT_APP_AWS_COGNITO_USER_POOL_WEBCLIENT_ID,
      Session: result.Session,
      ChallengeResponses: {
        USERNAME: email,
        ANSWER: 'EMAIL_OTP',
      },
    });

    if (selected.ChallengeName !== 'EMAIL_OTP' || !selected.Session) {
      throw new Error(`Unexpected challenge after selecting EMAIL_OTP: ${selected.ChallengeName}`);
    }

    return { username: email, session: selected.Session };
  }

  if (result.ChallengeName !== 'EMAIL_OTP' || !result.Session) {
    throw new Error(`Unexpected challenge: ${result.ChallengeName}`);
  }

  return { username: email, session: result.Session };
};

/*
 * Caches the tokens returned by a successful challenge response the same way
 * amazon-cognito-identity-js/Amplify does internally, so the rest of the app's
 * `Auth.currentSession()`/`Auth.currentAuthenticatedUser()` calls keep working unchanged.
 */
const cacheCognitoSession = (username: string, authResult: AuthenticationResult) => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: authResult.IdToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: authResult.AccessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: authResult.RefreshToken }),
  });
  cognitoUser.setSignInUserSession(session);

  // Hub-owned Cognito session includes a refresh token - clear any prior
  // student-hub SSO handoff marker so expiry can force-refresh normally.
  localStorage.removeItem(LOCALSTORAGE_SSO_HANDOFF_SESSION);
};

export const verifyEmailOtp = async (challenge: EmailOtpChallenge, code: string): Promise<void> => {
  let result: InitiateAuthResponse;
  try {
    result = await cognitoIdpRequest<InitiateAuthResponse>('RespondToAuthChallenge', {
      ChallengeName: 'EMAIL_OTP',
      ClientId: env.REACT_APP_AWS_COGNITO_USER_POOL_WEBCLIENT_ID,
      Session: challenge.session,
      ChallengeResponses: {
        USERNAME: challenge.username,
        EMAIL_OTP_CODE: code,
      },
    });
  } catch (e) {
    if (e instanceof CognitoIdpError && e.type === 'ExpiredCodeException') {
      throw new EmailOtpVerificationError('EXPIRED_CODE');
    }
    if (e instanceof CognitoIdpError && (e.type === 'CodeMismatchException' || e.type === 'NotAuthorizedException')) {
      throw new EmailOtpVerificationError('INVALID_CODE');
    }
    throw e instanceof CognitoIdpError ? new EmailOtpVerificationError('UNKNOWN') : e;
  }

  if (!result.AuthenticationResult) {
    // Some Cognito configs re-issue the same challenge instead of throwing on a wrong code.
    throw new EmailOtpVerificationError('INVALID_CODE');
  }

  cacheCognitoSession(challenge.username, result.AuthenticationResult);
};
